import React, { useState } from "react";
import { useForm, SubmitHandler } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { FaEnvelope } from "react-icons/fa";
import { useRequestPasswordReset } from "@/api/AuthApi";
import { handleApiErrors } from "@/utils/errorMapper";
import SmallLoader from "@/components/SmallLoader";
import SupportModal from "@/components/modals/SupportModal";

const ResetPasswordSchema = z.object({
  email: z
    .string()
    .email("Veuillez entrer une adresse email valide.")
    .min(1, "L'email est requis."),
});

type ResetPasswordFormData = z.infer<typeof ResetPasswordSchema>;

const ResetPassword: React.FC = () => {
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<ResetPasswordFormData>({
    resolver: zodResolver(ResetPasswordSchema),
  });

  const { requestPasswordReset, loading } = useRequestPasswordReset();
  const [emailSent, setEmailSent] = useState(false);

  const onSubmit: SubmitHandler<ResetPasswordFormData> = async (data) => {
    const result = await requestPasswordReset(data.email);
    if (result.success) {
      setEmailSent(true);
    } else if (result.error) {
      handleApiErrors(
        result.error,
        setError,
        "Impossible d'envoyer l'email de réinitialisation",
      );
    }
  };

  return (
    <div className="flex flex-col items-center justify-center h-screen bg-gray-100">
      <div className="absolute top-6 left-8 flex items-center space-x-2">
        <img
          src="../images/RAJAPI_logo.png"
          alt="Rajapi Logo"
          className="w-8 h-8"
        />
        <span className="text-xl font-extrabold text-blue-500">RAJAPI-COP</span>
      </div>

      <div className="bg-white p-8 rounded-lg shadow-lg max-w-sm w-full relative">
        {loading && <SmallLoader modal={true} />}

        <div className="flex flex-col items-center">
          <div className="text-blue-500 text-4xl mb-4">
            <img src="../images/modiflogo.png" />
          </div>
          <h2 className="text-lg font-semibold">
            Réinitialiser votre mot de passe
          </h2>
          <p className="text-sm text-gray-600 text-center mt-2">
            Entrez votre adresse email, et nous vous enverrons un lien pour
            créer un nouveau mot de passe.
          </p>
        </div>

        {emailSent ? (
          <div className="mt-6 text-center">
            <p className="text-sm text-gray-700">
              Si un compte existe avec cette adresse, un email contenant un lien
              de réinitialisation vient de vous être envoyé. Vérifiez votre
              boîte de réception.
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)} className="mt-6">
            <label
              htmlFor="email"
              className="text-sm font-medium text-gray-600"
            >
              Adresse Email
            </label>
            <div className="flex items-center mt-2">
              <FaEnvelope className="text-gray-400 text-xl mr-3" />
              <input
                id="email"
                type="email"
                placeholder="exemple@gmail.com"
                {...register("email")}
                className={`w-full p-2 border rounded-lg focus:outline-none focus:ring-2 ${
                  errors.email
                    ? "border-red-500 focus:ring-red-500"
                    : "border-gray-300 focus:ring-blue-500"
                }`}
              />
            </div>
            {errors.email && (
              <p className="text-red-500 text-sm mt-1">
                {errors.email.message}
              </p>
            )}

            <button
              type="submit"
              className="w-full mt-6 bg-blue-500 hover:bg-blue-600 text-white py-2 rounded-lg"
            >
              Envoyer
            </button>
          </form>
        )}

        <div className="mt-4">
          <SupportModal />
        </div>
      </div>

      <footer className="absolute bottom-4 text-gray-400 text-sm">
        © 2024 Kiosk
      </footer>
    </div>
  );
};

export default ResetPassword;
